// widget/src/bootloader.ts
// Per spec §3.1: script-tag entry point. Reads `data-vendor-id` from its own
// <script> tag, scans the page for the "Powered by Mikla" SEO badge (warns if
// missing per Q8c), and mounts a single <mikla-chat> custom element on the body.
//
// Imports widget.tsx for the side-effect of registering the custom element, and
// styles/tailwind.css so the bundler inlines the stylesheet text — widget.tsx's
// `import stylesText from './styles/tailwind.css'` returns that raw text via
// tsup's `loader: { '.css': 'text' }` rule.

import './widget.tsx'
import './styles/tailwind.css'

export function scanForBadge(): void {
  const links = document.querySelectorAll('a[href*="mikla.ai"]')
  if (links.length === 0) {
    console.warn(
      '[mikla-widget] Mikla SEO badge missing — paste the "Powered by Mikla" link in your footer.',
    )
  }
}

function readVendorId(): string | null {
  const scripts = document.querySelectorAll('script[data-vendor-id]')
  if (scripts.length === 0) return null
  // Last wins — if a vendor includes the snippet twice, prefer the most recent.
  const last = scripts[scripts.length - 1] as HTMLScriptElement
  return last.dataset.vendorId || null
}

export function init(): void {
  // Idempotency: if a vendor accidentally pastes the snippet twice (or if init()
  // is called more than once for any reason), do NOT mount a second widget.
  // Two <mikla-chat> elements would render two parallel chats on the page.
  if (document.querySelector('mikla-chat')) return
  scanForBadge()
  const vendorId = readVendorId()
  if (!vendorId) {
    console.error('[mikla-widget] No data-vendor-id on script tag — widget not mounted.')
    return
  }
  // Priority: runtime override (window.MIKLA_API_BASE) > build-time inject
  // (process.env.MIKLA_API_BASE, substituted by tsup) > hardcoded prod fallback.
  // Staging widget bundles ship with MIKLA_API_BASE=https://brain-staging.mikla.ai
  // baked in; prod bundles ship with https://brain.mikla.ai. The runtime override
  // exists for one-off testing without rebuilding.
  const apiBase =
    (window as unknown as { MIKLA_API_BASE?: string }).MIKLA_API_BASE ||
    process.env.MIKLA_API_BASE ||
    'https://brain.mikla.ai'
  const el = document.createElement('mikla-chat')
  el.setAttribute('vendor-id', vendorId)
  el.setAttribute('api-base', apiBase)
  document.body.appendChild(el)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
